import { YIN } from 'pitchfinder';

/** Pipeline YIN monofónico (Bordona) calibrado para metalófono Play. */
export const MONO_SAMPLE_RATE = 44100;
export const MONO_BUFFER_SIZE = 4096;
/** Sol5 ≈ 784 Hz — margen inferior para subarmónicos YIN. */
export const MONO_MIN_FREQ = 320;
/** Do7 / Si6 agudos del metalófono (~2100 Hz). */
export const MONO_MAX_FREQ = 2300;
export const MONO_DEFAULT_THRESHOLD = 0.0015;
/** Umbral relajado para ataques cortos del metalófono (cola del buffer). */
export const MONO_ATTACK_THRESHOLD = 0.00045;
export const MONO_GAIN = 3.0;
/** Ventana final del buffer (~35 ms) donde vive el transitorio del golpe. */
export const MONO_TAIL_SAMPLES = 1536;
export const MONO_FREQ_HISTORY = 4;
export const MONO_SILENCE_FRAMES = 6;
/** Salto > ~1 tono → nuevo ataque; limpiar historial para no arrastrar La↔Sol. */
export const MONO_PITCH_JUMP_CENTS = 105;
/** Centro sonoro típico octavas 5–7 (evita quedarse en subarmónicos ~500 Hz). */
export const METALophone_HZ_CENTER = 920;

export type PitchHzDetector = (buf: Float32Array) => number | null;

export function createPitchHzDetector(sampleRate: number): PitchHzDetector {
  const detectPrimary = YIN({
    sampleRate,
    threshold: 0.08,
    probabilityThreshold: 0.02,
  });
  const detectFallback = YIN({
    sampleRate,
    threshold: 0.16,
    probabilityThreshold: 0.02,
  });

  return (buf: Float32Array) => {
    let raw = detectPrimary(buf);
    if (!raw) raw = detectFallback(buf);
    if (!raw || raw < MONO_MIN_FREQ || raw > MONO_MAX_FREQ) return null;
    return raw;
  };
}

export function median(arr: number[]): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

export function stdDev(arr: number[], mean: number): number {
  if (arr.length < 2) return Infinity;
  const variance = arr.reduce((s, v) => s + (v - mean) * (v - mean), 0) / arr.length;
  return Math.sqrt(variance);
}

/** Si YIN ya da un Hz en rango del metalófono, confiar en él (no forzar otra octava). */
export function resolveOctave(rawFreq: number): number {
  if (rawFreq >= MONO_MIN_FREQ && rawFreq <= MONO_MAX_FREQ) {
    return rawFreq;
  }

  const up = rawFreq * 2;
  const down = rawFreq / 2;

  if (rawFreq < MONO_MIN_FREQ && up >= MONO_MIN_FREQ && up <= MONO_MAX_FREQ) {
    return up;
  }
  if (rawFreq > MONO_MAX_FREQ && down >= MONO_MIN_FREQ && down <= MONO_MAX_FREQ) {
    return down;
  }

  const candidates = [down, up].filter((f) => f >= MONO_MIN_FREQ && f <= MONO_MAX_FREQ);
  return candidates[0] ?? rawFreq;
}

export function computeRms(buf: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
  return Math.sqrt(sum / buf.length);
}

/** RMS de la cola del buffer: captura el pico del golpe aunque el promedio sea bajo. */
export function computePeakRms(buf: Float32Array, tailSamples = MONO_TAIL_SAMPLES): number {
  const start = Math.max(0, buf.length - tailSamples);
  const count = buf.length - start;
  if (count <= 0) return 0;
  let sum = 0;
  for (let i = start; i < buf.length; i++) sum += buf[i]! * buf[i]!;
  return Math.sqrt(sum / count);
}

export function measureVolume(buf: Float32Array): {
  rms: number;
  peakRms: number;
  volume: number;
} {
  const rms = computeRms(buf);
  const peakRms = computePeakRms(buf);
  const volume = Math.min(Math.max(rms, peakRms * 1.12) * 30, 1);
  return { rms, peakRms, volume };
}

/** ¿Hay señal útil? Umbral clásico + pico de ataque + subida respecto al ruido. */
export function isSignalPresent(
  volume: number,
  peakRms: number,
  prevPeakRms: number,
  noiseBaseline: number
): boolean {
  if (volume >= MONO_DEFAULT_THRESHOLD) return true;
  if (peakRms >= MONO_ATTACK_THRESHOLD) return true;
  if (noiseBaseline > 0 && peakRms >= noiseBaseline * 1.22) return true;
  if (
    prevPeakRms > 0 &&
    peakRms >= prevPeakRms * 1.15 &&
    peakRms >= Math.max(noiseBaseline, MONO_ATTACK_THRESHOLD * 0.5)
  ) {
    return true;
  }
  return false;
}

export function updateNoiseBaseline(
  baseline: number,
  rms: number,
  volume: number
): number {
  if (volume >= MONO_DEFAULT_THRESHOLD * 0.7) return baseline;
  if (baseline <= 0) return rms;
  return baseline * 0.94 + rms * 0.06;
}

/** @deprecated Usar detector ligado al AudioContext (sampleRate real). */
export function detectMonoPitchHz(buf: Float32Array, sampleRate = MONO_SAMPLE_RATE): number | null {
  return createPitchHzDetector(sampleRate)(buf);
}

export function smoothPitchHistory(
  history: number[],
  rawPitch: number,
  maxLen = MONO_FREQ_HISTORY
): { smoothed: number; confidence: number } {
  if (history.length >= 2) {
    const prev = history[history.length - 1]!;
    const jumpCents = Math.abs(1200 * Math.log2(rawPitch / prev));
    if (jumpCents > MONO_PITCH_JUMP_CENTS) {
      history.length = 0;
    }
  }

  history.push(rawPitch);
  if (history.length > maxLen) history.shift();

  if (history.length < 2) {
    return { smoothed: rawPitch, confidence: 0.5 };
  }

  const smoothed = median(history);
  const dev = stdDev(history, smoothed);
  const confidence = Math.max(0, 1 - dev / Math.max(smoothed, 1));
  return { smoothed, confidence };
}

export interface MonoAudioChainNodes {
  context: AudioContext;
  sampleRate: number;
  stream: MediaStream;
  analyser: AnalyserNode;
  buffer: Float32Array<ArrayBuffer>;
  detectPitch: PitchHzDetector;
}

/** Cadena DSP: highpass → compressor → gain → lowpass → analyser. */
export async function createMonoAudioChain(): Promise<MonoAudioChainNodes> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: true,
    },
  });

  const context = new AudioContext({ sampleRate: MONO_SAMPLE_RATE });
  if (context.state === 'suspended') await context.resume();

  const sampleRate = context.sampleRate;
  const detectPitch = createPitchHzDetector(sampleRate);

  const source = context.createMediaStreamSource(stream);
  const highpass = context.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 70;

  const compressor = context.createDynamicsCompressor();
  compressor.threshold.value = -50;
  compressor.knee.value = 40;
  compressor.ratio.value = 12;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.1;

  const gain = context.createGain();
  gain.gain.value = MONO_GAIN;

  const lowpass = context.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 6000;

  const analyser = context.createAnalyser();
  analyser.fftSize = MONO_BUFFER_SIZE;
  analyser.smoothingTimeConstant = 0;

  source.connect(highpass).connect(compressor).connect(gain).connect(lowpass).connect(analyser);

  return {
    context,
    sampleRate,
    stream,
    analyser,
    buffer: new Float32Array(MONO_BUFFER_SIZE),
    detectPitch,
  };
}

export function disposeMonoAudioChain(chain: MonoAudioChainNodes | null): void {
  if (!chain) return;
  chain.stream.getTracks().forEach((t) => t.stop());
  chain.context.close().catch(() => {});
}

/** Rango vocal por defecto (niños). Preferir perfiles en clingVoiceConfig. */
export const VOCAL_MIN_FREQ = 110;
export const VOCAL_MAX_FREQ = 750;
export const VOCAL_GAIN = 3.5;

export interface VocalDetectProfile {
  minHz: number;
  maxHz: number;
  hzCenter: number;
  preferFundamental: boolean;
  highpassHz: number;
  lowpassHz: number;
  gain: number;
}

export const DEFAULT_VOCAL_PROFILE: VocalDetectProfile = {
  minHz: VOCAL_MIN_FREQ,
  maxHz: VOCAL_MAX_FREQ,
  hzCenter: 330,
  preferFundamental: false,
  highpassHz: 55,
  lowpassHz: 2800,
  gain: VOCAL_GAIN,
};

export function createVocalPitchHzDetector(
  sampleRate: number,
  profile: VocalDetectProfile = DEFAULT_VOCAL_PROFILE
): PitchHzDetector {
  const detectPrimary = YIN({
    sampleRate,
    threshold: 0.06,
    probabilityThreshold: 0.008,
  });
  const detectFallback = YIN({
    sampleRate,
    threshold: 0.12,
    probabilityThreshold: 0.008,
  });

  return (buf: Float32Array) => {
    let raw = detectPrimary(buf);
    if (!raw) raw = detectFallback(buf);
    if (!raw) return null;
    // La resolución de octava la hace el juego (resolveSingOctave) con el rango La3–Do5.
    return raw;
  };
}

export function resolveVocalOctave(
  rawFreq: number,
  minHz = VOCAL_MIN_FREQ,
  maxHz = VOCAL_MAX_FREQ,
  hzCenter = 330,
  preferFundamental = false
): number {
  if (rawFreq <= 0) return rawFreq;

  const candidates = new Set<number>([rawFreq]);
  let up = rawFreq;
  let down = rawFreq;
  for (let i = 0; i < 5; i++) {
    up *= 2;
    down /= 2;
    candidates.add(up);
    candidates.add(down);
  }

  const inRange = [...candidates].filter((f) => f >= minHz && f <= maxHz);
  if (!inRange.length) return rawFreq;

  if (preferFundamental) {
    const sorted = [...inRange].sort((a, b) => a - b);
    for (const f of sorted) {
      const ratio = rawFreq / f;
      if (ratio >= 0.88 && ratio <= 2.12) return f;
    }
  }

  return inRange.reduce((best, f) =>
    Math.abs(Math.log2(f / hzCenter)) < Math.abs(Math.log2(best / hzCenter)) ? f : best
  );
}

/** Cadena DSP de Play adaptada a voz cantada (mismo compresor + YIN vocal). */
export async function createVocalAudioChain(
  profile: VocalDetectProfile = DEFAULT_VOCAL_PROFILE
): Promise<MonoAudioChainNodes> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: true,
    },
  });

  const context = new AudioContext({ sampleRate: MONO_SAMPLE_RATE });
  if (context.state === 'suspended') await context.resume();

  const sampleRate = context.sampleRate;
  const detectPitch = createVocalPitchHzDetector(sampleRate, profile);

  const source = context.createMediaStreamSource(stream);
  const highpass = context.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = profile.highpassHz;

  const compressor = context.createDynamicsCompressor();
  compressor.threshold.value = -50;
  compressor.knee.value = 40;
  compressor.ratio.value = 12;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.15;

  const gain = context.createGain();
  gain.gain.value = profile.gain;

  const lowpass = context.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = profile.lowpassHz;

  const analyser = context.createAnalyser();
  analyser.fftSize = MONO_BUFFER_SIZE;
  analyser.smoothingTimeConstant = 0;

  source.connect(highpass).connect(compressor).connect(gain).connect(lowpass).connect(analyser);

  return {
    context,
    sampleRate,
    stream,
    analyser,
    buffer: new Float32Array(MONO_BUFFER_SIZE),
    detectPitch,
  };
}
